import React, { useMemo } from 'react';
import {
  Controller,
  DefaultValues,
  useForm,
  FormProvider,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  addToScratchpadSchema,
  IAddToScratchpadFormState,
  NEW_SCRATCHPAD_SETTINGS,
} from './types';
import { FieldLabel, VerticalField, InputField } from '../../../Form';
import { ModalActions } from '../../../KubitModal/KubitModalUI';
import { PrimaryButton, SecondaryButton } from '../../../Button';
import ReactSelectMenu from '../../../Dropdown/StyledReactSelect/ReactSelectMenu';
import { useScratchpadContext } from '../../../Scratchpad/ScratchpadContext';

interface IProps {
  defaultValues: DefaultValues<IAddToScratchpadFormState>;
  onSubmit: (formData: IAddToScratchpadFormState) => void;
  onReset: () => void;
}

const AddToScratchpadForm = ({ defaultValues, onSubmit, onReset }: IProps) => {
  const formMethods = useForm<IAddToScratchpadFormState>({
    resolver: zodResolver(addToScratchpadSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting, isValid },
    control,
    watch,
  } = formMethods;

  const { scratchpads, loading } = useScratchpadContext();

  const scratchpadId = watch('scratchpadId');
  const name = watch('name');
  const isNewScratchpad = scratchpadId === NEW_SCRATCHPAD_SETTINGS.value;

  const isSaveEnabled =
    isValid && (!isNewScratchpad || (isNewScratchpad && name?.trim()));

  const options = useMemo(
    () => [
      NEW_SCRATCHPAD_SETTINGS,
      ...scratchpads.map((s) => ({
        value: s.id,
        label: s.title,
      })),
    ],
    [scratchpads],
  );

  return (
    <FormProvider {...formMethods}>
      <Controller
        name="scratchpadId"
        control={control}
        render={({ field }) => (
          <VerticalField>
            <ReactSelectMenu
              placeholder="Select an existing scratchpad"
              isLoading={loading}
              isClearable
              options={options}
              onChange={(selected) => field.onChange(selected?.value || '')}
              required
            />
          </VerticalField>
        )}
      />
      {isNewScratchpad && (
        <>
          <Controller
            name="name"
            control={control}
            render={({ field, fieldState }) => (
              <VerticalField>
                <FieldLabel>Scratchpad name</FieldLabel>
                <InputField
                  {...field}
                  autoComplete="off"
                  placeholder={'Add name'}
                  hasError={Boolean(fieldState.error)}
                />
              </VerticalField>
            )}
          />
        </>
      )}
      <ModalActions>
        <SecondaryButton
          type="button"
          onClick={onReset}
          disabled={isSubmitting}
          trackContext="reports"
          trackAction="add to scratchpad - cancel"
        >
          Cancel
        </SecondaryButton>
        <PrimaryButton
          type="button"
          disabled={isSubmitting || !isSaveEnabled}
          isGreyedOut={!isSaveEnabled}
          onClick={handleSubmit(onSubmit)}
          trackContext="reports"
          trackAction="add to scratchpad - save"
        >
          Save
        </PrimaryButton>
      </ModalActions>
    </FormProvider>
  );
};

export default AddToScratchpadForm;
